import {
	Stack,
	Button,
	Heading,
	Inline,
	Box,
	Spinner,
	CheckboxInput,
	RadioInput,
	Text,
	Grid,
	Col,
	Divider,
	Badge,
	Icons,
} from "@sembark-travel/ui-base"
import {
	ErrorMessage,
	Form,
	FormSpy,
	GetFieldValue,
	SubmissionError,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import {
	ConfirmationDialogProvider,
	ConfirmationDialog,
} from "@sembark-travel/ui-dialog"
import { useEffect, useMemo, useRef } from "react"
import useSWR from "swr"
import * as Validator from "yup"
import {
	EntityId,
	isPrimaryRemoved,
	MergeFormValues,
	useDebounce,
} from "./mergeUtils"

export function AsyncSearchList<T>({
	q,
	url,
	params,
	children,
}: {
	q: string
	url: string
	params?: Record<string, unknown>
	children: (props: { items: T[] }) => React.ReactNode
}) {
	const debouncedQ = useDebounce(q, 500)
	const xhr = useXHR()
	const shouldSearch = debouncedQ.length > 2
	const { data, isLoading } = useSWR(
		shouldSearch ? [url, debouncedQ, JSON.stringify(params ?? {})] : null,
		async () => {
			const resp = await xhr.get(url, {
				params: { q: debouncedQ, limit: 10, ...params },
			})
			return resp.data
		}
	)
	if (!shouldSearch) return <>{children({ items: [] })}</>
	if (isLoading) return <Spinner padding="4" alignCenter />
	return <>{children({ items: (data?.data ?? []) as T[] })}</>
}

export function MergeWarning({
	entityLabel,
	consequences,
}: {
	entityLabel: string
	consequences: string[]
}) {
	return (
		<Stack gap="2">
			<Heading fontSize="md" color="warning">
				Merging {entityLabel}s will:
			</Heading>
			<Stack gap="2" as="ul" paddingLeft="4" listStyleType="disc">
				{consequences.map((c) => (
					<Box as="li" key={c}>
						<Text>{c}</Text>
					</Box>
				))}
				<Box as="li" color="warning">
					<Icons.AttentionSolid />
					This is an irreversible process.
				</Box>
			</Stack>
			<Text fontWeight="semibold" color="warning">
				Are you sure you want to continue?
			</Text>
		</Stack>
	)
}

export function DuplicateEntityField<T>({
	title,
	description,
	url,
	params,
	getId,
	renderItem,
	renderSelected = renderItem,
	searchName = "q",
	duplicatesName = "duplicates",
	searchPlaceholder = "Type 3 or more characters to search...",
}: {
	title: string
	description: string
	url: string
	params?: Record<string, unknown>
	getId: (item: T) => EntityId
	renderItem: (item: T) => React.ReactNode
	/** how an already-selected item is shown (defaults to renderItem) */
	renderSelected?: (item: T) => React.ReactNode
	searchName?: string
	duplicatesName?: string
	searchPlaceholder?: string
}) {
	return (
		<Stack gap="4" position="relative">
			<Stack gap="4" position="sticky" top="0" bgColor="default">
				<Stack gap="1">
					<Heading fontSize="md">{title}</Heading>
					<Text color="muted">{description}</Text>
				</Stack>
				<TextInputField
					type="text"
					name={searchName}
					placeholder={searchPlaceholder}
					autoFocus
				/>
			</Stack>

			<GetFieldValue<T[]> name={duplicatesName}>
				{({ value: duplicates, onChange }) => (
					<GetFieldValue<string> name={searchName}>
						{({ value: q }) => (
							<AsyncSearchList<T> q={q} url={url} params={params}>
								{({ items }) => {
									const selectedIds = new Set(duplicates.map(getId))
									const rows = [
										...duplicates.map((item) => ({ item, selected: true })),
										...items
											.filter((i) => !selectedIds.has(getId(i)))
											.map((item) => ({ item, selected: false })),
									]
									return (
										<Stack
											borderWidth="1"
											rounded="md"
											overflow="auto"
											style={{ maxHeight: "300px" }}
										>
											{rows.map(({ item, selected }, index) => (
												<Box
													key={getId(item)}
													borderTopWidth={index > 0 ? "1" : undefined}
												>
													<Inline gap="4" as="label" paddingY="2" paddingX="4">
														<CheckboxInput
															checked={selected}
															onChange={() =>
																onChange(
																	selected
																		? duplicates.filter(
																				(d) => getId(d) !== getId(item)
																			)
																		: [...duplicates, item]
																)
															}
														/>
														<Box flex="1">
															{selected
																? renderSelected(item)
																: renderItem(item)}
														</Box>
													</Inline>
												</Box>
											))}
										</Stack>
									)
								}}
							</AsyncSearchList>
						)}
					</GetFieldValue>
				)}
			</GetFieldValue>

			<ErrorMessage name={duplicatesName} />
		</Stack>
	)
}

export function SelectPrimaryField<T>({
	entityLabel,
	getId,
	renderItem,
	duplicatesField = "duplicates",
	primaryField = "primary",
	headerTitle,
}: {
	entityLabel: string
	getId: (item: T) => EntityId
	renderItem: (item: T) => React.ReactNode
	duplicatesField?: string
	primaryField?: string
	headerTitle?: string
}) {
	const { value: duplicates = [] } = useFieldValue<T[]>(duplicatesField)
	const { value: primary, onChange } = useFieldValue<T | undefined>(
		primaryField
	)
	const label = entityLabel.toLowerCase()

	// clear primary if it was un-selected from duplicates
	useEffect(() => {
		if (primary && isPrimaryRemoved(primary, duplicates, getId)) {
			onChange(undefined)
		}
	}, [primary, duplicates, getId, onChange])

	return (
		<Stack gap="4" position="relative">
			<Stack gap="1" position="sticky" bgColor="default" top="0" zIndex="10">
				<Heading fontSize="md">
					{headerTitle ?? `Primary/Master ${entityLabel}`}
				</Heading>
				<Text color="muted">
					Please select the primary/master {label} from the duplicates
				</Text>
			</Stack>

			{duplicates.length <= 1 ? (
				<Box padding="4" bgColor="inset" rounded="md">
					<Text>Please select some duplicates to choose a primary {label}</Text>
				</Box>
			) : (
				<Stack borderWidth="1" rounded="md" overflow="hidden">
					{duplicates.map((item, index) => {
						const selected =
							primary !== undefined && getId(item) === getId(primary)
						return (
							<Box
								key={getId(item)}
								borderTopWidth={index > 0 ? "1" : undefined}
							>
								<Inline
									gap="4"
									as="label"
									paddingY="2"
									paddingX="4"
									bgColor={selected ? "primary" : { hover: "subtle" }}
								>
									<RadioInput
										checked={selected}
										onChange={(e) =>
											onChange(e.currentTarget.checked ? item : undefined)
										}
									/>
									<Box flex="1">{renderItem(item)}</Box>
								</Inline>
							</Box>
						)
					})}
				</Stack>
			)}
			<ErrorMessage name={primaryField} />
		</Stack>
	)
}

export function MergeConfirmation<T>({
	entityLabel,
	consequences,
	getId,
	renderItem,
	primaryField = "primary",
	duplicatesField = "duplicates",
	dialogTitle = `Merge ${entityLabel}s Confirmation`,
	acceptActionLabel = `Yes, Merge these ${entityLabel}s`,
}: {
	entityLabel: string
	consequences: string[]
	getId: (item: T) => EntityId
	renderItem: (item: T) => React.ReactNode
	primaryField?: string
	duplicatesField?: string
	dialogTitle?: string
	acceptActionLabel?: string
}) {
	return (
		<ConfirmationDialog
			title={dialogTitle}
			acceptActionLabel={acceptActionLabel}
		>
			<Stack gap="6">
				<FormSpy subscription={{ values: true }}>
					{({ values }) => {
						const primary: T | undefined = values[primaryField]
						const duplicates: T[] = values[duplicatesField] ?? []
						if (!primary) return null
						const items = [
							primary,
							...duplicates.filter((d) => getId(d) !== getId(primary)),
						]
						return (
							<Stack borderWidth="1" rounded="md" overflow="hidden">
								{items.map((item, i) => {
									const isPrimary = getId(item) === getId(primary)
									return (
										<Box
											key={getId(item)}
											bgColor={isPrimary ? "primary" : undefined}
											padding="2"
											borderTopWidth={i > 0 ? "1" : undefined}
										>
											{isPrimary && (
												<Badge primary>Primary {entityLabel}</Badge>
											)}
											{renderItem(item)}
										</Box>
									)
								})}
							</Stack>
						)
					}}
				</FormSpy>
				<MergeWarning entityLabel={entityLabel} consequences={consequences} />
			</Stack>
		</ConfirmationDialog>
	)
}

export function MergeEntities<T>({
	title,
	entityLabel,
	resetKey,
	renderDuplicates,
	renderPrimary,
	renderConfirmation,
	onSubmit,
	onCancel,
}: {
	title: string
	entityLabel: string
	/** `resetKey` returned from useMergeEntities() */
	resetKey: number
	renderDuplicates: () => React.ReactNode
	renderPrimary: () => React.ReactNode
	renderConfirmation: () => React.ReactNode
	onSubmit: (
		values: MergeFormValues<T>,
		confirm: () => Promise<boolean>
	) => Promise<void>
	onCancel: () => void
}) {
	const initialValues = useRef({
		q: "",
		primary: undefined,
		duplicates: [],
	} as unknown as MergeFormValues<T>).current

	const validate = useMemo(
		() =>
			validateFormValues(
				Validator.object().shape({
					duplicates: Validator.array().min(
						2,
						`Please select at least 2 duplicate ${entityLabel}s`
					),
					primary: Validator.mixed().required(
						`Please select a primary ${entityLabel}`
					),
				})
			),
		[entityLabel]
	)

	return (
		<Stack gap="6">
			<Heading>{title}</Heading>
			<ConfirmationDialogProvider key={resetKey}>
				{({ confirm }) => (
					<Form<MergeFormValues<T>>
						initialValues={initialValues}
						validate={validate}
						onSubmit={withServerErrors(async (values) => {
							await onSubmit(values, confirm)
						})}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form onSubmit={handleSubmit}>
								<Stack gap="4">
									<Grid gap="8">
										<Col sm={12} md>
											{renderDuplicates()}
										</Col>
										<Col sm={12} md={6}>
											{renderPrimary()}
										</Col>
									</Grid>
									<Divider sm />
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Merging..." : `Merge ${entityLabel}s`}
										</Button>
										<Button onClick={onCancel} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
									{renderConfirmation()}
								</Stack>
							</form>
						)}
					</Form>
				)}
			</ConfirmationDialogProvider>
		</Stack>
	)
}
